/**
 * Required-field completeness predicate, shared between the API and the
 * web client. Row shapes are structural so both Prisma rows and the web's
 * JSON-serialized cache satisfy them without coercion. Combine with
 * `hasAllRequiredIncomeYears` for the full "FD complete" check.
 */

import {
  AssetAccountType,
  FDItemType,
  InheritanceTrust,
} from "@prisma/client-generated";
import {
  type FDRequiredFieldsItemType,
  type RequiredFDField,
  getRequiredFieldsForItem,
  //isAccountBasedAsset,
  isMissingRequiredValue,
} from "./fd-required-fields.js";

/** Structural row carrying every required field for FDItemType `I`. */
type FDItemRow<I extends FDRequiredFieldsItemType> = Readonly<
  Partial<Record<RequiredFDField<I>, unknown>>
>;

// ACCOUNTS/ASSETS and INHERITANCE/TRUSTS each share a single Prisma model,
// so FDItemRow<ACCOUNTS> ≡ FDItemRow<ASSETS> and FDItemRow<INHERITANCE> ≡
// FDItemRow<TRUSTS> structurally. Pick one from each pair.
type AccountsAndAssetsRow = FDItemRow<typeof FDItemType.ACCOUNTS> & {
  readonly assetAccountType: AssetAccountType;
  readonly assetType?: string | null | undefined;
};

type InheritancesAndTrustsRow = FDItemRow<typeof FDItemType.INHERITANCE> & {
  readonly category: InheritanceTrust;
};

export interface FDCompletenessCollections {
  readonly accountsAndAssets: readonly AccountsAndAssetsRow[];
  readonly businesses: readonly FDItemRow<typeof FDItemType.BUSINESS>[];
  readonly childAccounts: readonly FDItemRow<
    typeof FDItemType.CHILD_ACCOUNTS
  >[];
  readonly debts: readonly FDItemRow<typeof FDItemType.DEBTS>[];
  readonly incomes: readonly (FDItemRow<typeof FDItemType.INCOME> & {
    readonly year: number | null;
  })[];
  readonly inheritancesAndTrusts: readonly InheritancesAndTrustsRow[];
  readonly realEstates: readonly FDItemRow<typeof FDItemType.REAL_ESTATE>[];
  readonly vehicles: readonly FDItemRow<typeof FDItemType.VEHICLES>[];
}

/** Per-item required-field check, generic so indexed access stays typed. */
export function hasAllRequiredFieldsForFDItem<
  I extends FDRequiredFieldsItemType,
>(itemType: I, item: FDItemRow<I>): boolean {
  return getRequiredFieldsForItem(itemType).every(
    (field) => !isMissingRequiredValue(item[field]),
  );
}

/**
 * `true` iff every FD item has all required fields filled in. Does not
 * check income-year coverage — see `hasAllRequiredIncomeYears`.
 */
export function allFDItemsHaveRequiredFields(
  c: FDCompletenessCollections,
): boolean {
  const isOk = hasAllRequiredFieldsForFDItem;
  return (
    c.accountsAndAssets.every(isAccountOrAssetItemComplete) &&
    c.childAccounts.every((i) => isOk(FDItemType.CHILD_ACCOUNTS, i)) &&
    c.vehicles.every((i) => isOk(FDItemType.VEHICLES, i)) &&
    c.incomes.every((i) => isOk(FDItemType.INCOME, i)) &&
    c.realEstates.every((i) => isOk(FDItemType.REAL_ESTATE, i)) &&
    c.businesses.every((i) => isOk(FDItemType.BUSINESS, i)) &&
    c.debts.every((i) => isOk(FDItemType.DEBTS, i)) &&
    c.inheritancesAndTrusts.every((i) =>
      isOk(
        i.category === InheritanceTrust.TRUST
          ? FDItemType.TRUSTS
          : FDItemType.INHERITANCE,
        i,
      ),
    )
  );
}

/**
 * AccountAndAsset rows split on `assetAccountType`: ACCOUNTs use the
 * ACCOUNTS field list; ASSETs use the ASSETS list, plus `institution` and
 * `accountNumber` when the asset is account-based.
 */
function isAccountOrAssetItemComplete(item: AccountsAndAssetsRow): boolean {
  if (item.assetAccountType === AssetAccountType.ACCOUNT) {
    return hasAllRequiredFieldsForFDItem(FDItemType.ACCOUNTS, item);
  }
  if (!hasAllRequiredFieldsForFDItem(FDItemType.ASSETS, item)) return false;
  // Removing institution/accountNumber check for account-based assets temporarily
  return true;
  /*
  if (!isAccountBasedAsset(item.assetType)) return true;
  return (
    !isMissingRequiredValue(item.institution) &&
    !isMissingRequiredValue(item.accountNumber)
  );
  */
}
